// Components
import SimpleMarkdownCell from '../../../components/molecules/SimpleTableCells/SimpleMarkdownCell/SimpleMarkdownCell';
import SimpleTextCell from '../../../components/molecules/SimpleTableCells/SimpleTextCell/SimpleTextCell';
import SimpleNumberCell from '../../../components/molecules/SimpleTableCells/SimpleNumberCell/SimpleNumberCell';
import SimpleTimeCell from '../../../components/molecules/SimpleTableCells/SimpleTimeCell/SimpleTimeCell';
import SimpleDateCell from '../../../components/molecules/SimpleTableCells/SimpleDateCell/SimpleDateCell';
import SimpleColorCell from '../../../components/molecules/SimpleTableCells/SimpleColorCell/SimpleColorCell';
import SimpleBooleanCell from '../../../components/molecules/SimpleTableCells/SimpleBooleanCell/SimpleBooleanCell';
import SimpleIconCell from '../../../components/molecules/SimpleTableCells/SimpleIconCell/SimpleIconCell';
import SimpleImageCell from '../../../components/molecules/SimpleTableCells/SimpleImageCell/SimpleImageCell';

// Domain
import { getDetailHeaderTableCellType } from '../../detailHeaderTable/detailHeaderTableUseCase';

// Styles
import styles from './renderCellByType.module.css';

/**
 * Render a table cell based on its type for SimpleTable
 * @param {ApiDetailHeaderTableCell} cell - The cell data
 * @param {'left' | 'center' | 'right'} align - Cell alignment
 * @param {{ markdownInline?: boolean }} [options] - Rendering options
 * @returns {JSX.Element | null}
 */
export const renderCellByType = (cell, align = 'left', options = {}) => {
  const { markdownInline = false } = options;
  if (!cell) return null;

  const { type, data } = getDetailHeaderTableCellType(cell);

  if (!type) return null;

  switch (type) {
    case 'text': {
      const textData = /** @type {ApiDetailHeaderTableCellText} */ (data);

      // Adapter le type DetailHeaderTable vers les composants SimpleTable
      let cellContent;

      switch (textData.type) {
        case 'text':
          cellContent = (
            <SimpleTextCell
              value={textData.value}
              align={align || 'left'}
              bold={false}
              italic={false}
            />
          );
          break;

        case 'number':
          cellContent = (
            <SimpleNumberCell
              value={textData.value}
              align={align || 'left'}
              bold={false}
              italic={false}
            />
          );
          break;

        case 'money':
          cellContent = (
            <SimpleNumberCell
              value={textData.value}
              align={align || 'left'}
              bold={false}
              italic={false}
              formatOptions={{
                style: 'currency',
                currency: 'EUR',
              }}
            />
          );
          break;

        case 'time':
          cellContent = (
            <SimpleTimeCell
              value={textData.value}
              align={align || 'left'}
              bold={false}
              italic={false}
            />
          );
          break;

        case 'date':
          cellContent = (
            <SimpleDateCell
              value={textData.value}
              align={align || 'left'}
              bold={false}
              italic={false}
            />
          );
          break;

        case 'dateTime':
          cellContent = (
            <SimpleDateCell
              value={textData.value}
              align={align || 'left'}
              bold={false}
              italic={false}
            />
          );
          break;

        case 'color':
          cellContent = (
            <SimpleColorCell
              value={textData.value}
              align={align || 'left'}
              bold={false}
              italic={false}
            />
          );
          break;

        default:
          // Fallback sur SimpleTextCell si type inconnu
          cellContent = (
            <SimpleTextCell
              value={textData.value}
              align={align || 'left'}
              bold={false}
              italic={false}
            />
          );
      }

      // Gérer le link et backgroundColor spécifiques à DetailHeaderTable
      if (textData.link) {
        return (
          <a
            href={textData.link}
            className={styles['table-cell-link']}
            style={textData.backgroundColor ? { backgroundColor: textData.backgroundColor } : {}}
          >
            {cellContent}
          </a>
        );
      }

      return (
        <div style={textData.backgroundColor ? { backgroundColor: textData.backgroundColor } : {}}>
          {cellContent}
        </div>
      );
    }

    case 'markdown': {
      const markdownData = /** @type {ApiDetailHeaderTableCellMarkdown} */ (data);
      return (
        <SimpleMarkdownCell
          value={markdownData.value}
          align={align}
          inline={markdownInline}
        />
      );
    }

    case 'boolean': {
      const booleanData = /** @type {ApiDetailHeaderTableCellBoolean} */ (data);
      return (
        <SimpleBooleanCell
          value={booleanData.value}
          trueIcon={booleanData.trueIcon}
          falseIcon={booleanData.falseIcon}
          align={align}
        />
      );
    }

    case 'icon': {
      const iconData = /** @type {ApiDetailHeaderTableCellIcon} */ (data);
      return (
        <SimpleIconCell
          icon={iconData.icon}
          color={iconData.color}
          url={iconData.url}
        />
      );
    }

    case 'image': {
      const imageData = /** @type {ApiDetailHeaderTableCellImage} */ (data);
      return (
        <SimpleImageCell
          imageUrl={imageData.imageUrl}
          link={imageData.link}
        />
      );
    }

    default:
      return <p>Unknown cell type</p>;
  }
};

export default renderCellByType;
